import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { getInviteDetails, acceptInvitation } from "../api/user";
import { useFormik } from "formik";
import * as Yup from "yup";
import { XCircleIcon, BuildingOfficeIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import PasswordInput from "../components/PasswordInput";

const InvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const userExists = !!inviteData?.userExists;

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  // One-click accept for existing users
  const handleExistingUserAccept = async () => {
    try {
      setAccepting(true);
      await acceptInvitation(token, {});
      toast.success("Invitation accepted! Please log in to access the company.");
      navigate("/login");
    } catch (err) {
      toast.error(err.message || "Failed to accept invitation");
    } finally {
      setAccepting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await acceptInvitation(token, {
          name: values.name,
          password: values.password,
        });
        toast.success("Invitation accepted successfully!");
        navigate("/login");
      } catch (error) {
        toast.error(error.message || "Failed to accept invitation");
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchInviteDetails = async () => {
      try {
        setLoading(true);
        const response = await getInviteDetails(token);
        setInviteData(response.data);
        setError(null);
      } catch (err) {
        setError(err.message || "Invalid or expired invite");
        toast.error(err.message || "Invalid or expired invite");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchInviteDetails();
    } else {
      setError("Invalid invite link");
      setLoading(false);
    }
  }, [token]);

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invite details...</p>
        </div>
      </div>
    );
  }

  if (error || !inviteData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full mx-4"
        >
          <div className="bg-white rounded-lg shadow-xl p-8 text-center">
            <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Invalid Invite
            </h1>
            <p className="text-gray-600 mb-6">
              {error || "This invitation link is invalid or has expired."}
            </p>
            <div className="space-y-3">
              <Link
                to="/login"
                className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary-dark transition-colors duration-200 inline-block"
              >
                Go to Login
              </Link>
              <Link
                to="/"
                className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition-colors duration-200 inline-block"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <img
                src="/Logo_EzSignly.png"
                alt="EzSignly"
                className="h-auto w-[200px] mx-auto"
              />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              You're Invited!
            </h1>
            <p className="text-gray-600">
              Join {inviteData.company?.name} on EzSignly
            </p>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-8">
              {/* Simple Invitation Info */}
              <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                  <BuildingOfficeIcon className="h-8 w-8 text-primary mr-3" />
                  <div className="text-left">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {inviteData.company?.name}
                    </h2>
                    <p className="text-sm text-gray-600">
                      Invited by {inviteData.invitedBy?.name || "N/A"}
                    </p>
                  </div>
                </div>
                <p className="text-gray-600 mb-6">
                  You've been invited to join as{" "}
                  <strong>{inviteData.role?.name}</strong>
                </p>
              </div>

              {/* Form or Approve Button */}
              {!showForm ? (
                <div className="text-center">
                  {inviteData.status === "PENDING" &&
                  !isExpired(inviteData.expiresAt) ? (
                    userExists ? (
                      <div className="space-y-3">
                        <p className="text-sm text-gray-500 mb-4">
                          You already have an account with this email. Click below to accept the invitation and gain access to{" "}
                          <strong>{inviteData.company?.name}</strong>.
                        </p>
                        <button
                          onClick={handleExistingUserAccept}
                          disabled={accepting}
                          className="bg-primary text-white py-3 px-8 rounded-md hover:bg-primary-dark transition-colors duration-200 font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {accepting ? "Accepting..." : "Accept Invitation"}
                        </button>
                      </div>
                    ) : (
                    <button
                      onClick={() => setShowForm(true)}
                      className="bg-primary text-white py-3 px-8 rounded-md hover:bg-primary-dark transition-colors duration-200 font-medium text-lg"
                    >
                      Approve Invitation
                    </button>
                    )
                  ) : inviteData.status === "ACCEPTED" ? (
                    <Link
                      to="/login"
                      className="bg-green-600 text-white py-3 px-8 rounded-md hover:bg-green-700 transition-colors duration-200 font-medium text-lg inline-block"
                    >
                      Login to Your Account
                    </Link>
                  ) : (
                    <div className="text-gray-500 py-3">
                      {inviteData.status === "REJECTED" &&
                        "This invitation was declined"}
                      {inviteData.status === "REVOKED" &&
                        "This invitation was revoked"}
                      {inviteData.status === "EXPIRED" &&
                        "This invitation has expired"}
                    </div>
                  )}
                </div>
              ) : (
                <div className="max-w-md mx-auto">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">
                    Complete Your Invitation
                  </h3>
                  <form onSubmit={formik.handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={inviteData.email}
                        disabled
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:border-transparent ${
                          formik.errors.name && formik.touched.name
                            ? "border-red-300 focus:ring-red-500"
                            : "border-gray-300 focus:ring-primary"
                        }`}
                        placeholder="Enter your full name"
                      />
                      {formik.errors.name && formik.touched.name && (
                        <p className="mt-1 text-sm text-red-600">
                          {formik.errors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password
                      </label>
                      <PasswordInput
                        name="password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:border-transparent ${
                          formik.errors.password && formik.touched.password
                            ? "border-red-300 focus:ring-red-500"
                            : "border-gray-300 focus:ring-primary"
                        }`}
                        placeholder="Enter your password"
                        error={
                          formik.errors.password && formik.touched.password
                            ? formik.errors.password
                            : undefined
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm Password
                      </label>
                      <PasswordInput
                        name="confirmPassword"
                        value={formik.values.confirmPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:border-transparent ${
                          formik.errors.confirmPassword &&
                          formik.touched.confirmPassword
                            ? "border-red-300 focus:ring-red-500"
                            : "border-gray-300 focus:ring-primary"
                        }`}
                        placeholder="Confirm your password"
                        error={
                          formik.errors.confirmPassword &&
                          formik.touched.confirmPassword
                            ? formik.errors.confirmPassword
                            : undefined
                        }
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowForm(false)}
                        className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition-colors duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={formik.isSubmitting}
                        className="flex-1 bg-primary text-white py-2 px-4 rounded-md hover:bg-primary-dark transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {formik.isSubmitting ? "Accepting..." : "Accept"}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Additional Info */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="text-center">
                  <p className="text-sm text-gray-500">
                    Need help?{" "}
                    <Link
                      to="/contact"
                      className="text-primary hover:text-primary-dark"
                    >
                      Contact Support
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InvitePage;
